/**
 * ProtectedRoute - 路由保护组件
 * 未登录用户将被重定向到登录页面
 */

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { prefetchAllFontsInIdle } from '../fontLoader';
import { startIdleAssetWarmup } from '../services/idleAssetWarmup';

/**
 * ProtectedRoute - 包裹需要登录才能访问的路由
 * @param {ReactNode} children - 子组件
 * @param {boolean} requireAdmin - 是否需要管理员权限
 */
export default function ProtectedRoute({ children, requireAdmin = false }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  /**
   * 字体后台预缓存：用户确认登录后静默触发
   *
   * 策略说明：
   *   - 触发时机：loading=false（token 已就绪）且 isAuthenticated=true
   *     确保 /api/fonts 请求携带有效 JWT，不会因未登录返回空数组
   *   - 覆盖范围：所有受保护页面（Dashboard / Editor）都经过此组件，
   *     用户每次打开浏览器只要处于已登录状态就会自动触发一次
   *   - 防重复：fontLoader 内部 _prefetchStarted 模块级标志保护，
   *     无论此 effect 执行多少次，字体下载任务在整个页面生命周期内只启动一次
   *   - 对用户无感知：内部延迟 3 秒 + requestIdleCallback，
   *     优先等页面核心内容加载完成，再利用浏览器空闲时间逐个串行下载（500ms/字体）
   *   - 缓存时效：字体文件存入 Service Worker Cache Storage（cineart-fonts-v1），
   *     Nginx 设置 expires 365d，用户清缓存后下次登录自动补齐
   */
  useEffect(() => {
    if (!loading && isAuthenticated) {
      prefetchAllFontsInIdle();
      startIdleAssetWarmup();
    }
  }, [loading, isAuthenticated]);

  // 正在加载登录状态
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <Loader2 size={32} style={styles.spinner} />
        <p style={styles.loadingText}>加载中...</p>
      </div>
    );
  }

  // 未登录，重定向到登录页
  if (!isAuthenticated) {
    // 保存当前路径，登录后可以跳回
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 需要管理员权限但不是管理员
  if (requireAdmin && !isAdmin) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>权限不足</h2>
        <p style={styles.errorText}>此页面需要管理员权限</p>
        <a href="/" style={styles.backLink}>返回首页</a>
      </div>
    );
  }

  return children;
}

const styles = {
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#050505',
    color: '#e2e8f0',
    gap: '16px',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
    color: '#666',
  },
  loadingText: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
  },
  errorContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f172a',
    color: '#e2e8f0',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  errorText: {
    fontSize: '14px',
    color: '#94a3b8',
    marginBottom: '24px',
  },
  backLink: {
    padding: '10px 20px',
    fontSize: '14px',
    color: '#fff',
    background: '#3b82f6',
    borderRadius: '8px',
    textDecoration: 'none',
  },
};
